import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, FileText, ClipboardCheck, CheckCircle, Clock, AlertCircle, ChevronRight, Upload, Loader2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { tutorService } from '../services/tutorService';
import { toast } from 'sonner';
import DemoBookingStats from '../components/DemoBookingStats';

const TutorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tutorData, setTutorData] = useState(null);
  const [statusData, setStatusData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('tutorData') || '{}');
      setTutorData(userData);

      const response = await tutorService.getStatus();
      setStatusData(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard load error:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!statusData) {
    return (
      <div className="max-w-6xl mx-auto">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">Please try refreshing the page.</p>
          <Button onClick={loadDashboardData}>Retry</Button>
        </Card>
      </div>
    );
  }

  const profileStatus = {
    profile: statusData.has_profile ? 'complete' : 'incomplete',
    documents: statusData.has_documents ? 'complete' : 'incomplete',
    test: statusData.has_completed_test ? statusData.user_status : 'not-taken',
  };

  const calculateProgress = () => {
    let completed = 0;
    if (profileStatus.profile === 'complete') completed += 33;
    if (profileStatus.documents === 'complete') completed += 33;
    if (profileStatus.test === 'approved' || profileStatus.test === 'active') completed += 34;
    else if (profileStatus.test === 'under_review') completed += 20;
    return completed;
  };

  const getOverallStatus = () => {
    if (statusData.user_status === 'approved' || statusData.user_status === 'active') {
      return { status: 'Active', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' };
    } else if (statusData.user_status === 'under_review') {
      return { status: 'Under Review', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' };
    } else if (statusData.user_status === 'rejected' || statusData.user_status === 'inactive') {
      return { status: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' };
    } else if (profileStatus.profile === 'complete' && profileStatus.documents === 'complete') {
      return { status: 'Ready for Test', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' };
    } else {
      return { status: 'Incomplete', color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' };
    }
  };

  const overallStatus = getOverallStatus();
  const progress = calculateProgress();

  const getStatusIcon = (status) => {
    if (status === 'complete' || status === 'approved' || status === 'active') {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (status === 'under_review') {
      return <Clock className="h-5 w-5 text-yellow-600" />;
    } else {
      return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const canTakeTest = profileStatus.profile === 'complete' && profileStatus.documents === 'complete' && profileStatus.test === 'not-taken';

  return (
    <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Tutor Dashboard</h1>
          <p className="text-gray-600">Welcome back, {tutorData?.fullName || 'Tutor'}!</p>
        </div>

        {/* Overall Status Card */}
        <Card className={`p-6 mb-8 ${overallStatus.bgColor} border-2 ${overallStatus.borderColor}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Profile Status</h2>
              <p className={`text-lg font-semibold ${overallStatus.color}`}>{overallStatus.status}</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-gray-900">{progress}%</div>
              <div className="text-sm text-gray-600">Complete</div>
            </div>
          </div>
          <Progress value={progress} className="h-3" />
          
          {statusData.user_status === 'under_review' && (
            <div className="mt-4 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-900">Profile Under Review</p>
                  <p className="text-sm text-yellow-800">Your profile and test have been submitted successfully. Our admin team will review it within 24-48 hours. You'll be notified via email once approved.</p>
                </div>
              </div>
            </div>
          )}
          
          {(statusData.user_status === 'approved' || statusData.user_status === 'active') && (
            <div className="mt-4 p-4 bg-green-100 border border-green-300 rounded-lg">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-900">Profile Approved!</p>
                  <p className="text-sm text-green-800">Congratulations! Your profile is now active and visible to students. You can start accepting tuition requests and demo bookings.</p>
                </div>
              </div>
            </div>
          )}
          
          {(statusData.user_status === 'rejected' || statusData.user_status === 'inactive') && (
            <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-900">Profile Rejected</p>
                  <p className="text-sm text-red-800">Unfortunately, your profile was not approved. Please contact support for more information or to resubmit your application.</p>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Demo Booking Stats - Only show if approved */}
        {(statusData.user_status === 'approved' || statusData.user_status === 'active') && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Demo Session Requests</h2>
              <Button
                onClick={() => navigate('/tutor/demo-bookings')}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                View All
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <DemoBookingStats />
          </div>
        )}

        {/* Action Items */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Profile Completion */}
          <Card className="p-6 hover:shadow-xl transition-all duration-300 border-gray-100 bg-white">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              {getStatusIcon(profileStatus.profile)}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Profile</h3>
            <p className="text-gray-600 text-sm mb-4">
              Add subjects, experience, bio, and availability
            </p>
            {profileStatus.profile === 'complete' ? (
              <Button
                onClick={() => navigate('/tutor/profile-completion')}
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                Edit Profile
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/tutor/profile-completion')}
                className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white"
              >
                Complete Now
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </Card>

          {/* Document Upload */}
          <Card className="p-6 hover:shadow-xl transition-all duration-300 border-gray-100 bg-white">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-blue-500 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              {getStatusIcon(profileStatus.documents)}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Documents</h3>
            <p className="text-gray-600 text-sm mb-4">
              Upload ID proof, degree certificates, and resume
            </p>
            {profileStatus.documents === 'complete' ? (
              <Button
                onClick={() => navigate('/tutor/document-upload')}
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                View Documents
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/tutor/document-upload')}
                className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white"
                disabled={profileStatus.profile !== 'complete'}
              >
                Upload Now
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </Card>

          {/* Skill Test */}
          <Card className="p-6 hover:shadow-xl transition-all duration-300 border-gray-100 bg-white">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-teal-600 rounded-xl flex items-center justify-center">
                <ClipboardCheck className="h-6 w-6 text-white" />
              </div>
              {getStatusIcon(profileStatus.test)}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Take Skill Test</h3>
            <p className="text-gray-600 text-sm mb-4">
              Complete assessment to activate your profile
            </p>
            {statusData.user_status === 'under_review' ? (
              <Button
                variant="outline"
                className="w-full border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                disabled
              >
                Under Review
              </Button>
            ) : (statusData.user_status === 'approved' || statusData.user_status === 'active') ? (
              <Button
                variant="outline"
                className="w-full border-green-300 text-green-700 hover:bg-green-50"
                disabled
              >
                Approved
              </Button>
            ) : (statusData.user_status === 'rejected' || statusData.user_status === 'inactive') ? (
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-50"
                disabled
              >
                Rejected
              </Button>
            ) : statusData.has_completed_test ? (
              <Button
                variant="outline"
                className="w-full border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                disabled
              >
                Test Completed - Under Review
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/tutor/skill-test')}
                className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white"
                disabled={!canTakeTest}
              >
                {canTakeTest ? 'Start Test' : 'Complete Profile First'}
                {canTakeTest && <ChevronRight className="ml-2 h-4 w-4" />}
              </Button>
            )}
          </Card>
        </div>

        {/* Instructions */}
        {profileStatus.test === 'not-taken' && (
          <Card className="p-6 bg-white border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Getting Started</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Complete Your Profile</h4>
                  <p className="text-sm text-gray-600">Add your teaching subjects, experience, bio, and availability details.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Upload Documents</h4>
                  <p className="text-sm text-gray-600">Upload your identity proof, degree certificates for verification.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Take the Skill Test</h4>
                  <p className="text-sm text-gray-600">Complete a quick assessment to demonstrate your teaching skills.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Admin Approval</h4>
                  <p className="text-sm text-gray-600">Our team will review your profile and test within 24-48 hours.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  5
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Start Teaching!</h4>
                  <p className="text-sm text-gray-600">Once approved, your profile will be visible to students and you can start accepting bookings.</p>
                </div>
              </div>
            </div>
          </Card>
        )}

    </div>
  );
};

export default TutorDashboard;